import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PartnerGoal, PartnerGoalUpdateInput } from "@colab/shared";

export type UpdateGoalInput = {
  id: string;
  /**
   * The partner_id this goal is currently bound to (or null for org-wide).
   * Needed so the hook can invalidate the rippling partner-detail and
   * goals.forPartner queries — the legacy `revalidatePath` cascaded through
   * `/admin/partners/${id}`.
   */
  partnerId: string | null;
  patch: PartnerGoalUpdateInput;
};

type UpdateGoalResponse = { ok: true; goal: PartnerGoal };

// PATCH /api/admin/goals/:id
export function useUpdateGoal() {
  const queryClient = useQueryClient();
  return useMutation<UpdateGoalResponse, ApiClientError, UpdateGoalInput>({
    mutationFn: ({ id, patch }) =>
      apiFetch<UpdateGoalResponse>(`/admin/goals/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, { id, partnerId, patch }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.detail(id) });
      if (partnerId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.goals.forPartner(partnerId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.partners.detail(partnerId),
        });
      }
      // Sub-bucket 19 — partner-scope edits. When the patch flips
      // partner_id, both the old AND the new partner's caches need a sweep
      // so the previous owner stops showing the goal and the new owner
      // picks it up.
      if (patch.partner_id !== undefined && patch.partner_id !== partnerId) {
        if (patch.partner_id) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.goals.forPartner(patch.partner_id),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.partners.detail(patch.partner_id),
          });
        }
      }
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update goal");
    },
  });
}
